import {
  SUIT_CHARS,
  SUIT_NAMES,
  WIND_CHARS,
  WIND_NAMES,
  DRAGON_CHARS,
  DRAGON_NAMES,
  HUMAN_INDEX,
  PLAYER_COUNT,
} from '../engine/constants.js';
import { isUsefulTile } from '../engine/ai.js';
import { handFit, tileFitsTarget } from '../engine/handMatch.js';

export function renderTile(t, cls = '', sm = false) {
  const c = cls + (sm ? ' tile-sm' : '');
  if (t.type === 'suited') {
    return `<div class="tile ${t.suit} ${c}"><span class="num">${t.num}</span><span class="suit-char">${SUIT_CHARS[t.suit]}</span></div>`;
  }
  if (t.type === 'wind') {
    return `<div class="tile wind ${c}"><span class="sym">${WIND_CHARS[t.honor]}</span><span class="wlabel">${WIND_NAMES[t.honor]}</span></div>`;
  }
  if (t.type === 'dragon') {
    const dc = 'dragon-' + t.honor;
    return `<div class="tile ${dc} ${c}"><span class="dragon-icon">🐉</span><span class="sym">${DRAGON_CHARS[t.honor]}</span></div>`;
  }
  if (t.type === 'flower') {
    return `<div class="tile flower ${c}"><span class="sym">🌸</span><span class="flabel">F</span></div>`;
  }
  return `<div class="tile joker ${c}"><span class="sym">🃏</span><span class="jlabel">J</span></div>`;
}

// `ui` is the ephemeral per-client UI state (selected tile index, claim state).
// `state` is the authoritative game state (engine output or server payload).
// `viewerIdx` is the engine player-array index of the viewer. Local mode
// passes 0 (the human is always index 0); online mode passes the index
// matching the client's seat.
export function renderGame(state, ui, canDeclareFn, viewerIdx = HUMAN_INDEX) {
  const wallEl = document.getElementById('gWall');
  wallEl.textContent = state.wall.length;
  // Threshold coloring: green default, yellow when getting low, red critical.
  wallEl.classList.remove('wall-low', 'wall-critical');
  if (state.wall.length < 15) wallEl.classList.add('wall-critical');
  else if (state.wall.length < 30) wallEl.classList.add('wall-low');
  wallEl.title = `${state.wall.length} tiles remaining (~${Math.ceil(state.wall.length / 4)} rounds)`;
  document.getElementById('gTurn').textContent = state.players[state.currentPlayer].name;
  document.getElementById('handCount').textContent = state.players[viewerIdx].hand.length;
  // Phase label doubles as the target-pattern badge.
  const phaseEl = document.getElementById('phaseLabel');
  if (ui.hintPattern && state.phase === 'play') {
    const p = ui.hintPattern; // a real 2026 card hand
    const hand = state.players[viewerIdx].hand;
    const fit = handFit(hand, p);
    phaseEl.innerHTML = `<span class="target-badge" id="targetBadge" title="Click to cycle / clear">🎯 ${p.label} · ${fit.score}/14 fit</span>`;
  } else {
    phaseEl.textContent =
      state.phase === 'charleston' ? 'Charleston' : state.phase === 'play' ? 'Playing' : '';
  }

  // Opponent row: everyone who isn't the viewer, in turn order starting from
  // the viewer's left neighbor.
  let ai = '';
  for (let step = 1; step < PLAYER_COUNT; step++) {
    const i = (viewerIdx + step) % PLAYER_COUNT;
    const p = state.players[i];
    const isCur = state.currentPlayer === i;
    const ncls = isCur ? 'ai-name current' : 'ai-name';
    const dot = isCur ? ' ⬤' : '';
    ai += `<div class="ai-slot"><div class="${ncls}">${p.name} (${p.seat})${dot}</div><div class="ai-backs">`;
    for (let j = 0; j < p.hand.length; j++) ai += '<div class="tile-back"></div>';
    ai += '</div>';
    if (p.exposed.length) {
      ai += '<div class="ai-exposed">';
      for (const grp of p.exposed) for (const t of grp.tiles) ai += renderTile(t, '', true);
      ai += '</div>';
    }
    ai += '</div>';
  }
  document.getElementById('aiRow').innerHTML = ai;

  // Discards
  let dp = '';
  for (let i = 0; i < state.discards.length; i++) {
    const cls = i === state.discards.length - 1 ? 'latest' : '';
    dp += renderTile(state.discards[i], cls, true);
  }
  document.getElementById('discardPool').innerHTML = dp;

  // Viewer's hand
  let ht = '';
  const hand = state.players[viewerIdx].hand;
  const target = ui.hintPattern || null;
  const fitInfo = target ? handFit(hand, target) : null;
  for (let i = 0; i < hand.length; i++) {
    let cls = '';
    if (i === ui.selectedTile) cls = 'selected';
    if (ui.recentDrawId != null && hand[i].id === ui.recentDrawId) {
      cls += ' just-drawn';
    }
    if (target && state.phase === 'play') {
      // Target-aware glow trumps the generic beginner hint.
      if (tileFitsTarget(hand[i], fitInfo.need)) cls += ' fit-glow';
      else cls += ' drop-glow';
    } else if (
      state.difficulty === 'beginner' &&
      state.phase === 'play' &&
      state.hasDrawn &&
      state.currentPlayer === viewerIdx &&
      !isUsefulTile(hand[i], hand)
    ) {
      cls += ' hint-glow';
    }
    ht += `<div data-tile-idx="${i}">${renderTile(hand[i], cls)}</div>`;
  }
  for (const grp of state.players[viewerIdx].exposed) {
    ht += '<div style="margin-left:8px;display:flex;gap:2px;border-left:2px solid var(--gold);padding-left:6px">';
    for (const t of grp.tiles) ht += renderTile(t, '');
    ht += '</div>';
  }
  document.getElementById('handTiles').innerHTML = ht;

  // Buttons
  const isMyTurn = state.currentPlayer === viewerIdx && state.phase === 'play';
  document.getElementById('btnDraw').disabled = !(isMyTurn && !state.hasDrawn && !ui.claimPending);
  document.getElementById('btnDiscard').disabled = !(isMyTurn && state.hasDrawn && ui.selectedTile >= 0);
  // 🃏 Swap appears only when a joker exchange is legal right now — the local
  // driver sets ui.swapCount; online mode never does, so it stays hidden.
  document.getElementById('btnSwap').style.display = ui.swapCount > 0 ? '' : 'none';
  document.getElementById('btnMahjong').disabled = !canDeclareFn(state);
  document.getElementById('btnHint').style.display = state.difficulty === 'advanced' ? 'none' : '';
}

export function renderCharTiles(state, picked, viewerIdx = HUMAN_INDEX) {
  let h = '';
  const hand = state.players[viewerIdx].hand;
  for (let i = 0; i < hand.length; i++) {
    const cls = picked.includes(i) ? 'picked' : '';
    h += `<div data-char-idx="${i}">${renderTile(hand[i], cls)}</div>`;
  }
  document.getElementById('charTiles').innerHTML = h;
  document.getElementById('charCount').textContent = picked.length + ' / 3 selected';
  document.getElementById('charBtn').disabled = picked.length !== 3;
}

export function setCommentary(msg) {
  const el = document.getElementById('gComm');
  if (el) el.textContent = msg;
}

export { SUIT_CHARS, SUIT_NAMES, WIND_CHARS, WIND_NAMES, DRAGON_CHARS, DRAGON_NAMES };
